/** What identifies an answer for whoever investigates it, and how long it asked to wait */
export interface Trace {
	request?: string;
	at?: string;
	retry?: number;
}

/**
 * A refusal the service gave, or the fact that it could not be reached.
 *
 * The distinction this class exists to preserve is the one the contract insists on: a refusal is an
 * answer, and an unreachable authority is not. A screen that shows "you do not have access" when the
 * network failed has told the person something untrue about their own permissions.
 */
export class Refusal extends Error {
	#code: string;
	get code() {
		return this.#code;
	}

	#status: number;
	get status() {
		return this.#status;
	}

	#details?: Record<string, unknown>;
	get details() {
		return this.#details;
	}

	#trace: Trace;
	/**
	 * The request's identifier, when the service answered with one (D43): what a person hands to whoever
	 * investigates, shown in the failure's "Technical details"
	 */
	get request(): string | undefined {
		return this.#trace.request;
	}

	/** When it was answered, or when it failed to be (ISO 8601) */
	get at(): string {
		return this.#trace.at ?? this.#made;
	}

	#made = new Date().toISOString();

	/** Seconds to wait before trying again, when the answer says (`RATE_LIMITED`, `retry-after`) */
	get retry(): number | undefined {
		const value = this.#trace.retry ?? Number(this.#details?.retry);
		return Number.isFinite(value) && value > 0 ? Math.ceil(value) : undefined;
	}

	constructor(code: string, message: string, status = 0, details?: Record<string, unknown>, trace: Trace = {}) {
		super(message);
		this.name = 'Refusal';
		this.#code = code;
		this.#status = status;
		this.#details = details;
		this.#trace = trace;
	}

	/** The identifier, time and wait the answer carried, for a copy of this refusal */
	get trace(): Trace {
		return { ...this.#trace, at: this.at };
	}

	/**
	 * The authority could not answer. Never treated as a refusal or as an approval.
	 *
	 * A server error counts: whatever failed there, it is not a verdict about the person, and showing
	 * one as "signed out" or "no access" would tell them something untrue.
	 */
	get unreachable(): boolean {
		return ['UNREACHABLE', 'UNANSWERED', 'UNSENT', 'UPSTREAM_UNAVAILABLE', 'UNAVAILABLE'].includes(this.#code) || (this.#status >= 500 && this.#status !== 501);
	}

	/**
	 * A change was sent and no answer came back, so it may or may not have been made. The screen
	 * says so and reads again rather than claiming either.
	 */
	get unknown(): boolean {
		return this.#code === 'UNANSWERED';
	}

	/** There is no valid identity. Signing in again is the useful next step */
	get unauthenticated(): boolean {
		return ['UNAUTHENTICATED', 'SESSION_EXPIRED', 'SESSION_REVOKED'].includes(this.#code);
	}

	/**
	 * A session that existed and is over, or an account that may no longer sign in: the answer says why
	 * a person is asked to sign in again, which "nobody is signed in" does not
	 */
	get ended(): boolean {
		return ['SESSION_EXPIRED', 'SESSION_REVOKED', 'ACCOUNT_SUSPENDED'].includes(this.#code);
	}

	/**
	 * The answer is about the session itself — none, expired, ended, or an account that may not sign in —
	 * and not about the request: the family's session rule answers it (`Client` gives it to the page's
	 * `Session`), never the screen that asked
	 */
	get lapsed(): boolean {
		return this.unauthenticated || this.#code === 'ACCOUNT_SUSPENDED';
	}

	/** Why the session ended, in `Session`'s words, when the answer says (`null` when it does not) */
	get reason(): 'expired' | 'revoked' | 'suspended' | null {
		const reasons: Record<string, 'expired' | 'revoked' | 'suspended'> = { SESSION_EXPIRED: 'expired', SESSION_REVOKED: 'revoked', ACCOUNT_SUSPENDED: 'suspended' };
		return reasons[this.#code] ?? null;
	}

	/**
	 * A request the session withheld (`Withheld`): the page's session dialog already answers the person,
	 * so no screen shows this as an error, and what the screen last read stays
	 */
	get quiet(): boolean {
		return false;
	}

	/**
	 * A destructive or irreversible action that met an ended session and was not sent again: the screen
	 * says so beside the action the person pressed (`Session`'s `unsent`), never as a failure
	 */
	get unsent(): boolean {
		return false;
	}

	/** The identity is valid and refused. Signing in again changes nothing */
	get denied(): boolean {
		return ['FORBIDDEN', 'ACCOUNT_SUSPENDED'].includes(this.#code);
	}

	/** The policy's reason in words, when the service said which rule a password broke */
	static #weak(reason: unknown): string {
		return ['short', 'long', 'common', 'breached', 'same', 'provider'].includes(String(reason)) ? `password.weak.${reason}` : 'password.weak';
	}

	/**
	 * The values a refusal's words name: how long to wait, the policy's minimum
	 */
	get values(): Record<string, string | number> {
		return { seconds: this.retry ?? 60, min: Number(this.#details?.min ?? 10) };
	}

	static is(value: unknown): value is Refusal {
		return value instanceof Error && value.name === 'Refusal';
	}

	/**
	 * What a screen keeps shown while it reads again: an outage stays, with its way out and the line
	 * saying when it last tried, until an answer replaces it; anything else is cleared
	 */
	static kept(current: Refusal | undefined): Refusal | undefined {
		return current?.unreachable ? current : undefined;
	}

	/**
	 * Runs a read and keeps in `keep` (a state setter) why it got nothing: nothing once it answers, the
	 * refusal when it refused, and the outage while it reads again (`kept`). Resolves whether Accounts
	 * answered, a refusal included, which takes the read out of the page's outage; a read the page's
	 * session withheld did not answer, and what was there stays.
	 */
	static async answered(work: () => Promise<unknown>, keep: (update: Refusal | undefined | ((current: Refusal | undefined) => Refusal | undefined)) => void): Promise<boolean> {
		keep(Refusal.kept);
		try {
			await work();
			keep(undefined);
			return true;
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return false;
			const given = Refusal.is(error) ? error : new Refusal('INTERNAL', String((error as Error)?.message ?? error));
			keep(given);
			return !given.unreachable;
		}
	}

	/**
	 * The catalog key that explains this to a person.
	 *
	 * Mapping happens here, once, so no screen invents its own wording for a code and no code reaches
	 * a person untranslated.
	 */
	get key(): string {
		const keys: Record<string, string> = {
			UNAUTHENTICATED: 'state.expired',
			SESSION_EXPIRED: 'state.expired',
			SESSION_REVOKED: 'state.revoked',
			ACCOUNT_SUSPENDED: 'state.suspended',
			REAUTHENTICATION_REQUIRED: 'identities.reauth',
			FORBIDDEN: 'state.denied',
			NOT_FOUND: 'state.notfound',
			VALIDATION_FAILED: 'error.validation',
			DESTINATION_REJECTED: 'error.validation',
			IDENTITY_IN_USE: 'identities.inuse',
			LAST_OWNER: 'members.lastowner',
			INVITATION_CLOSED: 'invitation.closed',
			INVITATION_EXPIRED: 'invitation.expired',
			ADDRESS_UNVERIFIED: 'invitation.unverified',
			STATE_INVALID: 'identities.last',
			EXCHANGE_INVALID: 'state.failed',
			PROVIDER_NOT_ENABLED: 'signin.unavailable',
			UNREACHABLE: 'error.network',
			UNANSWERED: 'state.unknown',
			UNSENT: 'state.unsent',
			RATE_LIMITED: 'state.limited',
			PASSWORD_WEAK: Refusal.#weak(this.#details?.reason),
			ADDRESS_UNCONFIRMED: 'signin.unconfirmed',
			CHALLENGE_REQUIRED: 'signin.challenge.short',
			IDENTITY_MATCHES_ACCOUNT: 'signin.matches.short',
			ACCOUNT_ERASED: 'signin.erased.short',
			FACTOR_INVALID: 'factor.invalid',
			TICKET_INVALID: 'factor.ticket',
			SLUG_UNAVAILABLE: 'error.slug.unavailable',
			ACCESS_CHANGED: 'permissions.changed',
			NAME_DUPLICATE: 'organizations.duplicate.short',
			CONFIRMATION_MISMATCH: 'error.confirmation',
			ORGANIZATION_CLOSING: 'closing.title',
			UPSTREAM_UNAVAILABLE: 'state.upstream',
			UNAVAILABLE: 'state.unavailable'
		};
		const key = keys[this.#code];
		if (key) return key;
		// An unexpected server failure is an outage to the person, and says so rather than "something
		// went wrong"
		return this.unreachable ? 'state.unavailable' : 'error.unexpected';
	}
}

/**
 * A request that met an ended session and is not sent again: the session was not renewed while it
 * waited, or it was a destructive action, which the person presses again (the family's session rule).
 * It keeps the refusal's code, and every screen leaves it quiet.
 */
export class Withheld extends Refusal {
	#unsent: boolean;

	/**
	 * @param refusal What the service answered
	 * @param unsent Whether it was a destructive action the person pressed (`replay: false`)
	 */
	constructor(refusal: Refusal, unsent: boolean) {
		super(refusal.code, refusal.message, refusal.status, refusal.details, refusal.trace);
		this.#unsent = unsent;
	}

	override get quiet(): boolean {
		return true;
	}

	override get unsent(): boolean {
		return this.#unsent;
	}
}
